import { FC } from "react";
import {
  Flex,
  Box,
  Text,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  IconButton,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { NavLink } from "react-router-dom";
import { IconLogout, IconUser, IconMenu2 } from "@tabler/icons-react";
import { useKeycloak } from "@Contexts/KeycloakContext";
import { HEADER_BG } from "../../../constants/env";
import { SIDEBAR_WIDTH } from "../Sidebar";

interface TopbarProps {
  onMenu: () => void;
  // Estado da sidebar fixa (desktop): controla o offset esquerdo e o aria-label.
  isSidebarOpen: boolean;
}

export const Topbar: FC<TopbarProps> = ({ onMenu, isSidebarOpen }) => {
  const { userInfo, logout } = useKeycloak();

  // No desktop o rótulo reflete a ação de alternar; no mobile abre o drawer.
  const desktopAriaLabel = isSidebarOpen ? "Fechar menu" : "Abrir menu";

  return (
    <Flex
      h="57px"
      bg={HEADER_BG}
      borderBottom="1px solid"
      borderColor="neutrals.stroke.soft-200"
      px={4}
      align="center"
      justify="space-between"
      position="fixed"
      top={0}
      left={{ base: 0, lg: isSidebarOpen ? SIDEBAR_WIDTH : "0px" }}
      right={0}
      zIndex={100}
      gap="16px"
      transition="left 0.2s ease"
    >
      {/* Esquerda no mobile: hambúrguer (abre drawer) + marca (no desktop a marca
          fica na sidebar). */}
      <HStack spacing={3} display={{ base: "flex", lg: "none" }} minW={0}>
        <IconButton
          aria-label="Abrir menu"
          icon={<Icon as={IconMenu2} boxSize={5} />}
          variant="ghost"
          size="sm"
          color="primary.light.onstate"
          _hover={{ bg: "rgba(255,255,255,0.16)" }}
          onClick={onMenu}
        />
        <NavLink to="/inicio" style={{ minWidth: 0 }}>
          <Text
            fontSize="sm"
            fontWeight="700"
            color="primary.light.onstate"
            fontFamily="heading"
            letterSpacing="-0.02em"
            noOfLines={1}
            cursor="pointer"
          >
            Rede Frota — Mapa de Rota
          </Text>
        </NavLink>
      </HStack>

      {/* Esquerda no desktop: hambúrguer que recolhe/expande a sidebar fixa.
          Quando a sidebar está recolhida também mostra a marca (link p/ início). */}
      <HStack spacing={3} display={{ base: "none", lg: "flex" }} minW={0}>
        <IconButton
          aria-label={desktopAriaLabel}
          icon={<Icon as={IconMenu2} boxSize={5} />}
          variant="ghost"
          size="sm"
          color="primary.light.onstate"
          _hover={{ bg: "rgba(255,255,255,0.16)" }}
          onClick={onMenu}
        />
        {!isSidebarOpen && (
          <NavLink to="/inicio" style={{ minWidth: 0 }}>
            <Text
              fontSize="sm"
              fontWeight="700"
              color="primary.light.onstate"
              fontFamily="heading"
              letterSpacing="-0.02em"
              noOfLines={1}
              cursor="pointer"
            >
              Rede Frota — Mapa de Rota
            </Text>
          </NavLink>
        )}
      </HStack>

      {/* Direita: usuário (ml=auto garante que fica à direita mesmo quando o
          bloco esquerdo some no desktop — senão o space-between o joga à esquerda) */}
      <HStack spacing="16px" h="32px" alignItems="center" ml="auto">
        <Divider orientation="vertical" borderColor="rgba(255,255,255,0.24)" h="24px" />

        <Menu>
          <MenuButton>
            <Flex align="center" gap={2} cursor="pointer">
              <Avatar
                size="sm"
                name={userInfo?.name}
                bg="rgba(255,255,255,0.24)"
                color="white"
                fontFamily="body"
                fontSize="xs"
                fontWeight="600"
              />
              {/* nome/e-mail escondidos em telas muito pequenas */}
              <Box textAlign="left" display={{ base: "none", sm: "block" }}>
                <Text
                  fontSize="sm"
                  fontWeight="500"
                  color="primary.light.onstate"
                  fontFamily="body"
                  lineHeight="1.2"
                  noOfLines={1}
                >
                  {userInfo?.name || "Usuário"}
                </Text>
                <Text
                  fontSize="xs"
                  color="rgba(255,255,255,0.72)"
                  fontFamily="body"
                  lineHeight="1.2"
                  noOfLines={1}
                >
                  {userInfo?.email}
                </Text>
              </Box>
            </Flex>
          </MenuButton>
          <MenuList>
            <MenuItem icon={<Icon as={IconUser} boxSize={4} />} isDisabled fontFamily="body">
              Perfil
            </MenuItem>
            <MenuItem
              icon={<Icon as={IconLogout} boxSize={4} />}
              onClick={() => logout()}
              color="states.error.base"
              fontFamily="body"
            >
              Sair
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>
    </Flex>
  );
};
